import { call, put, takeLatest, select } from 'redux-saga/effects';
import apiRequest from '@libs/apiRequest';
import { setSnack, setLoading } from 'utilities/redux.actions';
import * as T from './redux.actionTypes';
import * as actions from './redux.actions';
import * as selectors from './redux.selectors';
import { API_ENDPOINTS, getErrorMessage } from './common';

function* fetchTeamsSaga({ payload = {} }) {
    try {
        const filters = yield select(selectors.filtersSelector);
        const pagination = yield select(selectors.paginationSelector);
        const sort = yield select(selectors.sortSelector);

        const params = {
            limit: payload.limit || pagination.limit,
            offset: payload.offset || pagination.offset,
            'sort-by': payload.sortBy || sort.sortBy,
            'sort-descending': payload.sortDescending !== undefined ? payload.sortDescending : sort.sortDescending
        };
        const search = payload.search !== undefined ? payload.search : filters.search;
        const isActive = payload.isActive !== undefined ? payload.isActive : filters.isActive;
        if (search) params.search = search;
        if (isActive !== '' && isActive !== undefined && isActive !== null) params.isActive = isActive;

        yield put(setLoading(true));
        const response = yield call(apiRequest, { url: API_ENDPOINTS.GET_ALL, method: 'GET', params });
        yield put(actions.fetchTeamsSuccess(response.data));
    } catch (error) {
        const msg = getErrorMessage(error, 'Failed to fetch teams');
        yield put(actions.fetchTeamsFailure(msg));
        yield put(setSnack({ message: msg, type: 'error' }));
    } finally {
        yield put(setLoading(false));
    }
}

function* fetchTeamDetailSaga({ payload }) {
    try {
        yield put(setLoading(true));
        const response = yield call(apiRequest, {
            url: `${API_ENDPOINTS.GET_DETAIL}/${payload.id}/detail`,
            method: 'GET'
        });
        yield put(actions.fetchTeamDetailSuccess(response.data));
    } catch (error) {
        const msg = getErrorMessage(error, 'Failed to load team details');
        yield put(actions.fetchTeamDetailFailure(msg));
        yield put(setSnack({ message: msg, type: 'error' }));
    } finally {
        yield put(setLoading(false));
    }
}

function* createTeamSaga({ payload }) {
    try {
        const response = yield call(apiRequest, {
            url: API_ENDPOINTS.CREATE, method: 'POST', data: JSON.stringify(payload)
        });
        yield put(actions.createTeamSuccess(response.data));
        yield put(actions.closeTeamDialog());
        yield put(setSnack({ message: 'Team created successfully', type: 'success' }));
    } catch (error) {
        const msg = getErrorMessage(error, 'Failed to create team');
        yield put(actions.createTeamFailure(msg));
        yield put(setSnack({ message: msg, type: 'error' }));
    }
}

function* updateTeamSaga({ payload }) {
    try {
        const response = yield call(apiRequest, {
            url: `${API_ENDPOINTS.UPDATE}/${payload.id}`, method: 'PUT', data: JSON.stringify(payload.data)
        });
        yield put(actions.updateTeamSuccess(response.data));
        yield put(actions.closeTeamDialog());
        yield put(setSnack({ message: 'Team updated successfully', type: 'success' }));
        yield put(actions.fetchTeams());
    } catch (error) {
        const msg = getErrorMessage(error, 'Failed to update team');
        yield put(actions.updateTeamFailure(msg));
        yield put(setSnack({ message: msg, type: 'error' }));
    }
}

function* deleteTeamSaga({ payload }) {
    try {
        yield call(apiRequest, { url: `${API_ENDPOINTS.DELETE}/${payload.id}`, method: 'DELETE' });
        yield put(actions.deleteTeamSuccess(payload.id));
        yield put(actions.closeDeleteDialog());
        yield put(setSnack({ message: 'Team deleted successfully', type: 'success' }));
        yield put(actions.fetchTeams());
    } catch (error) {
        const msg = getErrorMessage(error, 'Failed to delete team');
        yield put(actions.deleteTeamFailure(msg));
        yield put(setSnack({ message: msg, type: 'error' }));
    }
}

function* addMemberSaga({ payload }) {
    try {
        const response = yield call(apiRequest, {
            url: `/team/${payload.teamId}/members`, method: 'POST', data: JSON.stringify(payload.data)
        });
        yield put(actions.addMemberSuccess(response.data));
        yield put(setSnack({ message: 'Member added', type: 'success' }));
    } catch (error) {
        yield put(setSnack({ message: getErrorMessage(error, 'Failed to add member'), type: 'error' }));
    }
}

function* updateMemberSaga({ payload }) {
    try {
        const response = yield call(apiRequest, {
            url: `/team/${payload.teamId}/members/${encodeURIComponent(payload.email)}`,
            method: 'PATCH', data: JSON.stringify(payload.data)
        });
        yield put(actions.updateMemberSuccess(response.data));
        yield put(setSnack({ message: 'Member updated', type: 'success' }));
    } catch (error) {
        yield put(setSnack({ message: getErrorMessage(error, 'Failed to update member'), type: 'error' }));
    }
}

function* removeMemberSaga({ payload }) {
    try {
        yield call(apiRequest, {
            url: `/team/${payload.teamId}/members/${encodeURIComponent(payload.email)}`, method: 'DELETE'
        });
        yield put(actions.removeMemberSuccess(payload.email));
        yield put(setSnack({ message: 'Member removed', type: 'success' }));
    } catch (error) {
        yield put(setSnack({ message: getErrorMessage(error, 'Failed to remove member'), type: 'error' }));
    }
}

function* refetchOnChange() { yield put(actions.fetchTeams()); }

function* fetchEmployeeOptionsSaga() {
    try {
        const response = yield call(apiRequest, {
            url: '/employee/get/all/preview-role-based',
            method: 'GET',
            params: { limit: 500 }
        });
        const data = response?.data?.data || response?.data || [];
        yield put(actions.fetchEmployeeOptionsSuccess(Array.isArray(data) ? data : []));
    } catch (error) {
        yield put(actions.fetchEmployeeOptionsFailure(getErrorMessage(error, 'Failed to load employees')));
        yield put(actions.fetchEmployeeOptionsSuccess([]));
    }
}

export default function* teamManagementSaga() {
    yield takeLatest(T.FETCH_TEAMS, fetchTeamsSaga);
    yield takeLatest(T.FETCH_TEAM_DETAIL, fetchTeamDetailSaga);
    yield takeLatest(T.CREATE_TEAM, createTeamSaga);
    yield takeLatest(T.UPDATE_TEAM, updateTeamSaga);
    yield takeLatest(T.DELETE_TEAM, deleteTeamSaga);
    yield takeLatest(T.ADD_MEMBER, addMemberSaga);
    yield takeLatest(T.UPDATE_MEMBER, updateMemberSaga);
    yield takeLatest(T.REMOVE_MEMBER, removeMemberSaga);
    yield takeLatest(T.FETCH_EMPLOYEE_OPTIONS, fetchEmployeeOptionsSaga);
    yield takeLatest([T.SET_FILTERS, T.SET_PAGINATION, T.SET_SORT], refetchOnChange);
}
